import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Heart, Users, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";

const HERO = "https://images.unsplash.com/photo-1637264546948-f00b2b9b2ed7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxjYXRob2xpYyUyMG1lbiUyMHByYXlpbmclMjByb3NhcnklMjBjaHVyY2h8ZW58MHx8fHwxNzkwNDI1NjA3fDA&ixlib=rb-4.1.0&q=85&w=1600";
const SANCTUARY = "https://images.unsplash.com/photo-1759670483359-37aa2b159673?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxldXJvcGVhbiUyMGNhdGhvbGljJTIwY2h1cmNoJTIwY2F0aGVkcmFsJTIwaW50ZXJpb3IlMjBzYW5jdHVhcnl8ZW58MHx8fHwxNzkwNDI1NjA3fDA&ixlib=rb-4.1.0&q=85&w=1200";

export default function Home() {
  const { t } = useI18n();
  const [stats, setStats] = useState({ pledges: 0, communities: 0, countries: 0 });

  useEffect(() => {
    Promise.all([api.get("/prayer/stats"), api.get("/communities"), api.get("/communities/meta/countries")])
      .then(([p, c, co]) => setStats({ pledges: p.data.pledges, communities: c.data.length, countries: co.data.length }))
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-th-blue text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-th-blue/85 via-th-blue/90 to-th-bluedark" />
        </div>
        <div className="th-hero-glow absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-36">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] font-semibold text-th-gold">{t("hero_eyebrow")}</span>
            <h1 className="mt-5 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t("hero_title")}
            </h1>
            <p className="mt-6 text-lg text-white/80 leading-relaxed max-w-2xl">{t("hero_subtitle")}</p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <Link to="/aderir" data-testid="hero-join-btn">
                <Button size="lg" className="bg-th-gold text-th-bluedark hover:bg-th-goldlight font-semibold rounded-full px-8 h-13 text-base group">
                  {t("hero_cta_join")} <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/comunidades" data-testid="hero-find-btn">
                <Button size="lg" variant="outline" className="border-white/40 text-white bg-white/5 hover:bg-white/15 rounded-full px-8 h-13 text-base">
                  <MapPin className="mr-2 w-4 h-4" /> {t("hero_cta_find")}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-th-bluedark text-white border-t border-th-gold/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-3 gap-4 text-center">
          {[
            { icon: Heart, value: stats.pledges.toLocaleString(), label: t("stat_men") },
            { icon: Users, value: stats.communities, label: t("stat_communities") },
            { icon: MapPin, value: stats.countries, label: t("stat_countries") },
          ].map((s, i) => (
            <div key={i} data-testid={`home-stat-${i}`}>
              <s.icon className="w-6 h-6 text-th-gold mx-auto mb-2" />
              <div className="font-display font-bold text-3xl md:text-4xl">{s.value}</div>
              <div className="text-xs uppercase tracking-widest text-white/50 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-th-gold">{t("section_mission")}</span>
          <h2 className="mt-4 font-display font-semibold text-3xl md:text-4xl text-th-blue tracking-tight leading-tight">
            {t("hero_title")}
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">{t("mission_text")}</p>
          <div className="mt-8 flex gap-4">
            <Link to="/quem-somos"><Button variant="outline" className="border-th-blue text-th-blue rounded-full" data-testid="home-about-btn">{t("nav_about")}</Button></Link>
            <Link to="/rosario-pela-paz"><Button className="bg-th-blue text-white rounded-full hover:bg-th-bluelight" data-testid="home-prayer-btn">{t("nav_prayer")}</Button></Link>
          </div>
        </div>
        <div className="relative">
          <div className="rounded-3xl overflow-hidden shadow-2xl">
            <img src={SANCTUARY} alt="Santuário" className="w-full h-[420px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 flex items-center gap-3 border border-th-gold/20">
            <Logo size={44} />
            <div>
              <div className="font-display font-bold text-th-blue text-sm">Mãe Rainha</div>
              <div className="text-xs text-slate-500">Europa · Desde a fé</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-th-blue th-pattern">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <Calendar className="w-8 h-8 text-th-gold mx-auto mb-4" />
          <h2 className="font-display font-semibold text-2xl md:text-3xl text-white">{t("events_title")}</h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto">{t("events_subtitle")}</p>
          <Link to="/eventos"><Button className="mt-7 bg-th-gold text-th-bluedark hover:bg-th-goldlight rounded-full px-8" data-testid="home-events-btn">{t("events_title")}<ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
        </div>
      </section>
    </div>
  );
}
